interface Props {
  /** Rendered pixel height of the mark. Width scales from the asset's aspect ratio. */
  size?: number
  /** Makes the mark a real button. In the header this is the route home. */
  onClick?: () => void
  /** Accessible name for the interactive form. Ignored when `onClick` is absent. */
  label?: string
}

// Sampled from the supplied logo artwork; lighter than tokens.gold, which is
// the PMS 873 accent used elsewhere on the chrome.
const LOGO_GOLD = '#ad9b68'

/**
 * Keystone brand mark — a solid gold keystone (trapezoid), no wordmark.
 * Renders inline SVG only.
 *
 * The browser-tab favicon (public/favicon.svg, public/favicon.ico) redraws the
 * same polygon on a dark tile — change the points here and there together.
 *
 * With `onClick` it renders a `<button>`, not a clickable div: since the
 * nav dropped to the design's three tabs, the mark is the only route home,
 * and a div would have put that route out of reach of the keyboard.
 * Without `onClick` it is pure decoration and is hidden from assistive tech.
 */
export function TrelleborgMark({ size = 40, onClick, label = 'Keystone — home' }: Props) {
  const artwork = (
    <svg
      width={(size * 979) / 1024}
      height={size}
      viewBox="0 0 979 1024"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <polygon fill={LOGO_GOLD} points="0,0 979,0 824,1024 155,1024" />
    </svg>
  )

  const layout = {
    display: 'inline-flex',
    alignItems: 'center',
    userSelect: 'none',
    height: size,
  } as const

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        style={{
          ...layout,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        {artwork}
      </button>
    )
  }

  return (
    <div style={{ ...layout, cursor: 'default' }} aria-hidden>
      {artwork}
    </div>
  )
}
